/**
 * CheckinSearch Component
 *
 * Campo de busca para locais de check-in.
 * Suporta busca por GPS (locais próximos) e por texto.
 *
 * @since 2025-12-17
 * @module tmf_checkin
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';

interface CheckinSearchProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (value: string) => void;
  onGpsSearch: () => void;
  placeholder?: string;
  loading?: boolean;
  gpsLoading?: boolean;
  gpsError?: string | null;
  autoFocus?: boolean;
}

export function CheckinSearch({
  value,
  onChange,
  onSearch,
  onGpsSearch,
  placeholder = 'Buscar por nome ou código...',
  loading = false,
  gpsLoading = false,
  gpsError = null,
  autoFocus = false,
}: CheckinSearchProps) {
  const gpsLoadingText = loading ? 'Buscando locais próximos...' : 'Obtendo localização...';
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Auto focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle input change with debounce
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      // Debounce search
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (newValue.length >= 2) {
          onSearch(newValue);
        }
      }, 300);
    },
    [onChange, onSearch]
  );

  // Handle key press
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && value.length >= 2) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        onSearch(value);
      }
    },
    [onSearch, value]
  );

  // Clear input
  const handleClear = useCallback(() => {
    onChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div
        className={clsx(
          'relative flex items-center bg-muted border rounded-lg transition-colors',
          isFocused
            ? 'border-orange-500 ring-1 ring-orange-500'
            : 'border-border'
        )}
      >
        {/* Search icon */}
        <div className="flex items-center pl-3">
          {loading ? (
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <i className="bi bi-search text-muted-foreground" />
          )}
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2.5 bg-transparent text-sm text-foreground placeholder-muted-foreground focus:outline-none"
        />

        {/* Clear button */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <i className="bi bi-x-lg text-sm" />
          </button>
        )}
      </div>

      {/* GPS button */}
      <button
        type="button"
        onClick={onGpsSearch}
        disabled={gpsLoading}
        className={clsx(
          'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors',
          gpsLoading
            ? 'bg-muted text-muted-foreground cursor-not-allowed'
            : gpsError
            ? 'bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/30'
            : 'bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-500/30'
        )}
      >
        {gpsLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">{gpsLoadingText}</span>
          </>
        ) : gpsError ? (
          <>
            <i className="bi bi-exclamation-triangle" />
            <span className="text-sm font-medium">Erro: {gpsError}</span>
            <span className="text-xs">(clique para tentar novamente)</span>
          </>
        ) : (
          <>
            <i className="bi bi-geo-alt-fill" />
            <span className="text-sm font-medium">Usar minha localização</span>
          </>
        )}
      </button>

      {/* Help text */}
      <p className="text-xs text-muted-foreground text-center">
        Busque por nome ou código do local, ou use o GPS para encontrar locais próximos
      </p>
    </div>
  );
}

export default CheckinSearch;
